import { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext.js';
import Layout from '../components/Layout.js';
import { useAppTranslation } from '../hooks/useAppTranslation.js';
import { Check } from 'lucide-react';

interface OrgSettings {
  default_organisation?: string;
  allow_theme_customisation?: boolean;
  allow_image_customisation?: boolean;
  allow_links_customisation?: boolean;
  allow_privacy_customisation?: boolean;
}

interface Organisation {
  id: string;
  name: string;
  slug: string;
}

export default function Settings() {
  const { t } = useAppTranslation();
  const { user, loading: authLoading } = useAuth();
  const [org, setOrg] = useState<Organisation | null>(null);
  const [settings, setSettings] = useState<OrgSettings>({});
  const [name, setName] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    Promise.all([
      fetch('/api/organisations/current', { credentials: 'include' }).then((r) => r.json()).catch(() => null),
      fetch('/api/admin/settings', { credentials: 'include' }).then((r) => r.json()).catch(() => null),
    ]).then(([orgData, settingsData]) => {
      if (orgData) {
        setOrg(orgData);
        setName(orgData.name ?? '');
      }
      if (settingsData) {
        setSettings(settingsData as OrgSettings);
      }
    }).catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  async function handleSave(e: React.FormEvent) {
    e.preventDefault();
    setSaved(false);
    setSaving(true);
    try {
      if (org) {
        await fetch(`/api/organisations/${org.id}`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ name }),
        });
      }
      const flags = {
        allow_theme_customisation: settings.allow_theme_customisation ?? false,
        allow_image_customisation: settings.allow_image_customisation ?? false,
        allow_links_customisation: settings.allow_links_customisation ?? false,
        allow_privacy_customisation: settings.allow_privacy_customisation ?? false,
      };
      await fetch('/api/admin/settings', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(flags),
      });
      setSaved(true);
    } catch {
      console.error;
    } finally {
      setSaving(false);
    }
  }

  if (authLoading || loading) {
    return (
      <Layout title={t('nav.settings')}>
        <div className="p-8 text-center text-ink-faint">{t('common.loading')}</div>
      </Layout>
    );
  }

  const inputCls = 'block w-full rounded-lg bg-panel border border-line px-3 py-2 text-sm text-ink placeholder:text-ink-faint focus:outline-none focus:border-accent-500/50 focus:ring-1 focus:ring-accent-500/30 transition';

  return (
    <Layout title={t('nav.settings')}>
      <div className="max-w-xl space-y-6">
        <form onSubmit={handleSave} className="space-y-5">

          {/* Organisation */}
          <div className="rounded-xl bg-panel border border-line p-6 space-y-4">
            <h2 className="text-sm font-semibold text-ink">{t('settings.section.organisation')}</h2>
            <div>
              <label className="block text-xs font-medium text-ink-muted mb-1.5">{t('common.name')}</label>
              <input
                type="text"
                required
                maxLength={100}
                value={name}
                onChange={(e) => setName(e.target.value)}
                className={inputCls}
              />
              {org?.slug && (
                <p className="mt-1.5 text-xs text-ink-faint">{t('settings.organisation.slug')}: {org.slug}</p>
              )}
            </div>
          </div>

          {/* Feature flags */}
          <div className="rounded-xl bg-panel border border-line p-6 space-y-4">
            <div>
              <h2 className="text-sm font-semibold text-ink">{t('settings.section.featureFlags')}</h2>
              <p className="mt-1 text-xs text-ink-faint">
                {t('settings.featureFlags.description')}
              </p>
            </div>
            <div className="space-y-3">
              {[
                { key: 'allow_theme_customisation', label: t('settings.featureFlags.theme') },
                { key: 'allow_image_customisation', label: t('settings.featureFlags.images') },
                { key: 'allow_links_customisation', label: t('settings.featureFlags.links') },
                { key: 'allow_privacy_customisation', label: t('settings.featureFlags.privacy') },
              ].map(({ key, label }) => (
                <label key={key} className="flex items-center gap-3 cursor-pointer group">
                  <div className="relative flex-shrink-0">
                    <input
                      type="checkbox"
                      checked={Boolean(settings[key as keyof OrgSettings])}
                      onChange={(e) =>
                        setSettings((prev) => ({
                          ...prev,
                          [key]: e.target.checked,
                        }))
                      }
                      className="sr-only peer"
                    />
                    <div className="h-5 w-5 rounded border border-line-strong bg-panel peer-checked:bg-accent-600 peer-checked:border-accent-600 transition-all flex items-center justify-center">
                      <svg className="hidden peer-checked:block w-3 h-3 text-white" viewBox="0 0 12 12" fill="none">
                        <path d="M2 6l3 3 5-5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </div>
                  </div>
                  <span className="text-sm text-ink-muted group-hover:text-ink transition-colors">{label}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Account info */}
          {user && (
            <div className="rounded-xl bg-panel border border-line p-6 space-y-4">
              <h2 className="text-sm font-semibold text-ink">{t('settings.section.account')}</h2>
              <div className="space-y-2.5">
                {[
                  { label: t('common.email'), value: user.email },
                  { label: t('common.role'), value: user.role },
                  { label: t('settings.account.emailVerified'), value: user.emailVerified ? t('common.yes') : t('common.no') },
                ].map(({ label, value }) => (
                  <div key={label} className="flex justify-between items-center text-sm">
                    <span className="text-ink-muted">{label}</span>
                    <span className="text-ink capitalize">{value}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="flex items-center gap-4 pt-1">
            <button
              type="submit"
              disabled={saving}
              className="rounded-full bg-accent-600 px-6 py-2.5 text-sm font-semibold text-white hover:opacity-90 disabled:opacity-50 transition-opacity"
            >
              {saving ? t('settings.saving') : t('settings.saveSettings')}
            </button>
            {saved && (
              <span className="inline-flex items-center gap-1.5 text-sm text-success-400">
                <Check className="h-4 w-4" /> {t('settings.saved')}
              </span>
            )}
          </div>
        </form>
      </div>
    </Layout>
  );
}
